import type { PublicUser } from '@spawnwire/shared';
import type { AppState } from '../store/store';

/**
 * The composer shows readable mentions (@username, @Role Name, #channel) and
 * converts them to stable id tokens (<@id>, <@&id>, <#id>) when sending.
 */
function splitCode(text: string): { code: boolean; text: string }[] {
  const parts: { code: boolean; text: string }[] = [];
  const re = /```[\s\S]*?```|`[^`\n]*`/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) parts.push({ code: false, text: text.slice(last, m.index) });
    parts.push({ code: true, text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ code: false, text: text.slice(last) });
  return parts;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function mentionableUsers(state: AppState, channelId: string): PublicUser[] {
  const channel = state.channels[channelId];
  if (!channel) return [];
  const root = channel.type === 'thread' && channel.parentId ? state.channels[channel.parentId] : channel;
  if (!root) return [];
  if (!root.spaceId) return (root.recipients ?? []).map((r) => state.users[r.id] ?? r);
  const members = Object.values(state.members[root.spaceId] ?? {}).map((m) => state.users[m.user.id] ?? m.user);
  const me = state.myMembers[root.spaceId];
  if (me && !members.some((u) => u.id === me.user.id)) members.push(me.user);
  return members;
}

export function encodeMentions(text: string, state: AppState, channelId: string): string {
  const channel = state.channels[channelId];
  const root = channel?.type === 'thread' && channel.parentId ? state.channels[channel.parentId] : channel;
  const spaceId = root?.spaceId ?? null;
  const users = new Map(mentionableUsers(state, channelId).map((u) => [u.username, u.id]));
  const roles = spaceId ? (state.spaces[spaceId]?.roles ?? []).filter((r) => !r.isDefault).sort((a, b) => b.name.length - a.name.length) : [];
  const channels = spaceId ? Object.values(state.channels).filter((c) => c.spaceId === spaceId && c.type !== 'category' && c.type !== 'thread') : [];
  const byName = new Map(channels.map((c) => [c.name, c.id]));

  return splitCode(text)
    .map((part) => {
      if (part.code) return part.text;
      let t = part.text;
      for (const role of roles) {
        t = t.replace(new RegExp(`(^|[\\s(])@${escapeRe(role.name)}(?![\\p{L}\\p{N}_])`, 'gu'), (_m, pre: string) => `${pre}<@&${role.id}>`);
      }
      t = t.replace(/(^|[\s(])@([a-z0-9_.]{2,32})(?![a-z0-9_])/g, (m, pre: string, name: string) => (users.has(name) ? `${pre}<@${users.get(name)}>` : m));
      t = t.replace(/(^|[\s(])#([\p{L}\p{N}_-]{1,100})/gu, (m, pre: string, name: string) => (byName.has(name) ? `${pre}<#${byName.get(name)}>` : m));
      return t;
    })
    .join('');
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

export function decodeMentions(text: string, state: AppState): string {
  return text
    .replace(new RegExp(`<@(${UUID})>`, 'g'), (m, id: string) => (state.users[id] ? `@${state.users[id]!.username}` : m))
    .replace(new RegExp(`<@&(${UUID})>`, 'g'), (m, id: string) => {
      for (const s of Object.values(state.spaces)) {
        const role = s.roles.find((r) => r.id === id);
        if (role) return `@${role.name}`;
      }
      return m;
    })
    .replace(new RegExp(`<#(${UUID})>`, 'g'), (m, id: string) => (state.channels[id] ? `#${state.channels[id]!.name}` : m));
}
